import React, { useEffect, useReducer, useRef } from 'react';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import { convertNumberInput } from '../../../utils/validations';
import { generalConversionReducer, generalConversionInitialState, GeneralConversionState } from './reducer';
import { useConversion } from '../../ConversionContext';
import { getConvertedUnit } from '../../../../components/bearing-search/api/api';
import { ConversionUnitsPayload } from '../../../../components/bearing-search/types/types';
import { mapConversionAnalyicsData } from '../../Utils/AnalyticsUtils';
import { ConversionToolTabProps } from '../../types';
import { tabInputDebounceFunction } from '../Utils/TabUtils';
import { trackScientificNotationChangeEvent } from "../../../utils/analytics";

const TabGeneral: React.FC<ConversionToolTabProps> = ({ description }) => {
	const { exponent, precision } = useConversion();

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const lastInputRef = useRef<{
		category: 'length' | 'mass' | 'load' | 'torque' | 'power';
		unit: string;
		value: string;
	} | null>(null);

	const lastResetCategoryRef = useRef<'length' | 'mass' | 'load' | 'torque' | 'power' | null>(null);
	// Reducer
	const [ generalConversionState, dispatch ] = useReducer(
		generalConversionReducer,
		generalConversionInitialState,
	);

	const currentStateRef = useRef();

	/**
	 * Recalculate all conversions units
	 */
	const recalculateAllConversions = async () => {
		let changedCategories = [];

		for (const [ categoryKey, units ] of Object.entries(generalConversionState)) {
			const category = categoryKey as keyof GeneralConversionState;

			for (const [ unit, value] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					if (!changedCategories.includes(category)){
						changedCategories.push(category);
					}
					if (!changedCategories.includes('precision exponent') && exponent) {
						changedCategories.push('precision exponent');
					}
					if (!changedCategories.includes('precision') && precision != 4) {
						changedCategories.push('precision');
					}
					callAndDispatchConversion(category, unit, value,true);
				}
			}
		}

		return changedCategories;
	};

	/**
	 * Reset all conversions units
	 */
	const resetAllConversions = () => {
		for (const [ categoryKey, units] of Object.entries(generalConversionState)) {
			const category = categoryKey as keyof GeneralConversionState;

			for (const [ value ] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					dispatch({
						type: 'reset_category',
						payload: {
							category,
						},
					});
				}
			}
		}
	};

	/**
	 * Update the converted values with the new exponent
	 * convert based on the first input of each category
	 */
	useEffect(() => {
		currentStateRef.current = generalConversionState;
		recalculateAllConversions().then((result) => {
			trackScientificNotationChangeEvent('conversionTool', result);
		});
	}, [ exponent ]);

	/**
	 * Reset the values when precision is changed
	 */
	useEffect(() => {
		resetAllConversions();
	}, [ precision ]);

	// Map of conversion types for API payload
	const conversionTypeMap: Record<string, string[]> = {
		mm: [ 'mmToinch' ],
		in: [ 'inchTomm' ],
		kg: [ 'kgToPound' ],
		lb: [ 'poundTokg' ],
		n: [ 'newtonTopoundForce' ],
		lbf: [ 'poundForceToNewton' ],
		nm: [ 'NmTolbfInch', 'newtonMetersToFootPounds' ],
		inlbf: [ 'lbfInchToNm', 'inchPoundsToFootPounds' ],
		ftlbf: [ 'footPoundsToNewtonMeters', 'footPoundToInchPounds' ],
		kw: [ 'kWTohp', 'kilowattsToDINHorsepower' ],
		hp: [ 'hpTokw', 'saeHorsepowerToDINHorsepower' ],
		hpdin: [ 'dinHorsepowerToKilowatts', 'dinHorsepowerToSAEHorsepower' ],
	};

	// Map of converted units from API response
	const convertedUnitMap: Record<string, string> = {
		mmToinch: 'in',
		inchTomm: 'mm',
		kgToPound: 'lb',
		poundTokg: 'kg',
		newtonTopoundForce: 'lbf',
		poundForceToNewton: 'n',
		NmTolbfInch: 'inlbf',
		newtonMetersToFootPounds: 'ftlbf',
		lbfInchToNm: 'nm',
		inchPoundsToFootPounds: 'ftlbf',
		footPoundsToNewtonMeters: 'nm',
		footPoundToInchPounds: 'inlbf',
		kWTohp: 'hp',
		hpTokw: 'kw',
		kilowattsToDINHorsepower: 'hpdin',
		saeHorsepowerToDINHorsepower: 'hpdin',
		dinHorsepowerToKilowatts: 'kw',
		dinHorsepowerToSAEHorsepower: 'hp',
	};

	/**
	 * @param {('length' | 'mass' | 'load' | 'torque' | 'power')} category - The category of the conversion.
	 * @param {string}                                            unit     - The unit of measurement.
	 * @param {string}                                            value    - The value to be converted.
	 * @param {boolean}											  skipAnalyticCall  - If Precision or Scientific is changed, skip analytic call
	 */
	const callAndDispatchConversion = async (
		category: 'length' | 'mass' | 'load' | 'torque' | 'power',
		unit: string,
		value: string,
		skipAnalyticCall: boolean
	) => {
		const conversionTypeArray = conversionTypeMap[ unit ] || [];
		if (conversionTypeArray.length === 0) {
			return;
		}

		if (skipAnalyticCall == undefined) {
			skipAnalyticCall = false;
		}

		const conversionPayload: ConversionUnitsPayload = {
			unitConversionInputs: [
				{
					conversionType: conversionTypeArray,
					inputValue: value,
					precision,
					exponent,
				},
			],
		};

		try {
			const response = await getConvertedUnit(conversionPayload);

			// ANALYTICS
			if (!skipAnalyticCall) {
				mapConversionAnalyicsData(category, conversionPayload, response);
			}

			if (response.unitConversionResults && category !== lastResetCategoryRef.current) {
				for (const conversionResult of response.unitConversionResults) {
					const convertedType = conversionResult.convertedTypes;
					const convertedValue = conversionResult.convertedValue;
					const convertedUnit = convertedUnitMap[ convertedType ];

					if (! convertedUnit) {
						console.log(`${ convertedType } index doesn't exit in convertedUnitMap`);
						return;
					}

					const converted = {
						[ convertedUnit ]: convertedValue.replaceAll(/,/g, ''),
					};

					dispatch({
						type: 'update_unit',
						payload: {
							category,
							updatedUnits: converted,
						},
					});
				}
			} else {
				dispatch({
					type: 'reset_category',
					payload: {
						category,
					},
				});
			}
		} catch (error) {
			console.error('Conversion API failed', error);
		}
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 */
	const handleInputChange = (
		category: 'length' | 'mass' | 'load' | 'torque' | 'power',
		unit: string,
		value: string,
	) => {
		const numericValue = convertNumberInput(value);
		if (numericValue === undefined) {
			dispatch({
				type: 'reset_category',
				payload: { category },
			});

			lastResetCategoryRef.current = category;

			return; // return, don't call API
		}

		dispatch({
			type: 'update_unit',
			payload: {
				category,
				updatedUnits: { [ unit ]: value },
			},
		});

		lastResetCategoryRef.current = null;
		lastInputRef.current = { category, unit, value };

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}

		const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);
		debounceTimeout.current = setTimeout(() => {
			tabInputDebounceFunction(lastInputRef, callAndDispatchConversion);
		}, debounceDelay);
	};

	/**
	 * @param currentValue
	 * @param  category
	 * @param  unit
	 * @param  newValue
	 */
	const handleBlur = (
		currentValue: string,
		category: 'length' | 'mass' | 'load' | 'torque' | 'power',
		unit: string,
		newValue: string,
	) => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
			debounceTimeout.current = null;
		}

		const numericValue = Number.parseFloat(newValue);
		if (Number.isNaN(numericValue) || numericValue === 0) {
			return;
		}

		if (currentValue == newValue) {
			return;
		}

		callAndDispatchConversion(category, unit, newValue, false);
	};

	return (
		<>
			{/* Length */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Length</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-length-mm"
								label={''}
								unitLabel={'mm'}
								value={generalConversionState.length.mm}
								min={0}
								onChange={(e) => handleInputChange('length', 'mm', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value,'length', 'mm', generalConversionState.length.mm ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-length-in"
								label={''}
								unitLabel={'in'}
								value={generalConversionState.length.in}
								min={0}
								onChange={(e) => handleInputChange('length', 'in', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'length', 'in', generalConversionState.length.in ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Mass */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Mass</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-mass-kg"
								label={''}
								unitLabel={'kg'}
								value={generalConversionState.mass.kg}
								min={0}
								onChange={(e) => handleInputChange('mass', 'kg', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'mass', 'kg', generalConversionState.mass.kg ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-mass-lb"
								label={''}
								unitLabel={'lb'}
								value={generalConversionState.mass.lb}
								min={0}
								onChange={(e) => handleInputChange('mass', 'lb', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'mass', 'lb', generalConversionState.mass.lb ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Load */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Load</span>
				</div>
				<div className="col-12 col-md-8">
					<div className='row align-items-center mx-md-0'>
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-load-n"
								label={''}
								unitLabel={'N'}
								value={generalConversionState.load.n}
								min={0}
								onChange={(e) => handleInputChange('load', 'n', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'load', 'n', generalConversionState.load.n ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-load-lbf"
								label={''}
								unitLabel={'lbf'}
								value={generalConversionState.load.lbf}
								min={0}
								onChange={(e) => handleInputChange('load', 'lbf', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'load', 'lbf', generalConversionState.load.lbf ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Torque */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Torque</span>
				</div>
				<div className="col-12 col-md-8">
					<div className='row align-items-center mx-md-0'>
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-torque-nm"
								label={''}
								unitLabel={'N-m'}
								value={generalConversionState.torque.nm}
								min={0}
								onChange={(e) => handleInputChange('torque', 'nm', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'torque', 'nm', generalConversionState.torque.nm ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-torque-inlbf"
								label={''}
								unitLabel={'in-lbf'}
								value={generalConversionState.torque.inlbf}
								min={0}
								onChange={(e) => handleInputChange('torque', 'inlbf', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'torque', 'inlbf', generalConversionState.torque.inlbf ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-torque-ftlbf"
								label={''}
								unitLabel={'ft-lbf'}
								value={generalConversionState.torque.ftlbf}
								min={0}
								onChange={(e) => handleInputChange('torque', 'ftlbf', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'torque', 'ftlbf', generalConversionState.torque.ftlbf ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Power */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Power</span>
				</div>
				<div className="col-12 col-md-8">
					<div className='row align-items-center mx-md-0'>
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-power-kw"
								label={''}
								unitLabel={'kW'}
								value={generalConversionState.power.kw}
								min={0}
								onChange={(e) => handleInputChange('power', 'kw', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'power', 'kw', generalConversionState.power.kw ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-power-hp"
								label={''}
								unitLabel={'HP'}
								value={generalConversionState.power.hp}
								min={0}
								onChange={(e) => handleInputChange('power', 'hp', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'power', 'hp', generalConversionState.power.hp ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-hpdin"
								label={''}
								unitLabel={'HP (DIN)'}
								value={generalConversionState.power.hpdin}
								min={0}
								onChange={(e) => handleInputChange('power', 'hpdin', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'power', 'hpdin', generalConversionState.power.hpdin ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default TabGeneral;
